import { ReactElement } from 'react';
import { Link, useRoutes } from 'react-router-dom';
import { ticketStates, type Ticket, type TicketState } from 'modules/support/types';
import { ModelScene, EntityScene, DetailsScene, IndexScene } from 'components';
import { SelectFilterChip } from 'components/filters/SelectFilterChip';
import { never } from 'core/permissions';
import { parseISO } from 'date-fns';
import { ViewTicket } from './View';
import { TickStateTag } from 'modules/support/SupportStateTag';
import { createOptions, Option } from 'options';
import { formatCustomSince } from 'utilities';
import { UserAvatar } from 'components/UserAvatar';
import { ControlledTableColumns, flexColumn } from 'components/ControlledTable';

const cols: ControlledTableColumns<Ticket> = [
  {
    content: 'State',
    width: 110,
    key: 'state',
    sortable: true,
    render: ({ state }: Ticket) => <TickStateTag state={state} appearance="naked" />,
  },
  flexColumn({
    content: 'Ticket',
    key: 'number',
    required: true,
    render: ({ id, title }: Ticket) => (
      <>
        <strong className="mr-2">#{id}</strong>
        <Link to={`${id}`}>{title}</Link>
      </>
    ),
    sortable: true,
    minWidth: 200,
  }),
  {
    content: 'Agency',
    key: 'enterprise.company.title',
    width: 200,
  },

  {
    content: 'Created',
    key: 'created_at',
    render: ({ created_at }: Ticket) => formatCustomSince(parseISO(created_at)),
    width: 140,
  },
  {
    content: 'Author',
    key: 'author',
    cellProps: { textAlign: 'center' },
    width: 90,
    render: ({ author }: Ticket) => <UserAvatar user={author} size="xs" />,
  },
  {
    content: 'Assignee',
    key: 'assignee',
    cellProps: { textAlign: 'center' },
    width: 90,
    render: ({ assignee }: Ticket) => (assignee ? <UserAvatar user={assignee} size="xs" /> : null),
  },
];

const model = {
  endpoint: 'support/tickets/',
  title: 'Ticket',
  pluralTitle: 'Tickets',
  entityTitle: (e: Ticket) => `#${e.id}`,
  //   permissions,
};

export const ticketStateOptions: Option<TicketState>[] = createOptions(ticketStates);

export function Tickets(): ReactElement {
  const routes = useRoutes([
    {
      index: true,
      element: (
        <IndexScene
          columns={cols}
          canCreate={never}
          storageKey="dashboard_tickets"
          filterFields={<SelectFilterChip name="state" options={ticketStateOptions} />}
        />
      ),
    },
    {
      path: ':id/',
      element: <EntityScene />,
      children: [
        {
          index: true,
          element: (
            <DetailsScene heading={null}>
              <ViewTicket />
            </DetailsScene>
          ),
        },
      ],
    },
  ]);
  return <ModelScene {...model}>{routes}</ModelScene>;
}
